import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useAuth} from '../../App';
import {AlertCircle, Shield, ArrowLeft} from 'lucide-react';

const RoleBasedRoute = ({
                            children,
                            allowedRoles,
                            redirectTo = '/login',
                            fallbackComponent = null
                        }) => {
    const {isAuthenticated, user, isLoading} = useAuth();
    const location = useLocation();

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Checking permissions...</p>
                </div>
            </div>
        );
    }

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return (
            <Navigate
                to={redirectTo}
                state={{from: location}}
                replace
            />
        );
    }

    // Check if user has required role
    // Check both 'role' and 'userType' fields for compatibility
    const userRole = user?.role || user?.userType || 'tenant'; // Default to 'tenant' if no role specified
    const hasRequiredRole = allowedRoles.length === 0 || allowedRoles.includes(userRole);

    // If user doesn't have required role, show access denied or redirect
    if (!hasRequiredRole) {
        // If a custom fallback component is provided, use it
        if (fallbackComponent) {
            return fallbackComponent;
        }

        // Default access denied page
        return (
            <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <div
                            className="mx-auto h-16 w-16 bg-murram-100 rounded-full flex items-center justify-center mb-6">
                            <Shield className="h-8 w-8 text-murram-500"/>
                        </div>
                        <h2 className="font-display text-3xl font-bold text-soil-900 mb-2">Access Denied</h2>
                        <p className="text-soil-600 mb-6">
                            You don't have permission to access this page.
                        </p>
                    </div>
                </div>

                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow-xl rounded-xl sm:px-10">
                        <div className="mb-6 p-4 bg-murram-100/40 border border-murram-100 rounded-lg flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-murram-500 mt-0.5"/>
                            <div>
                                <h3 className="text-murram-600 font-medium mb-1">Insufficient Permissions</h3>
                                <p className="text-murram-600 text-sm">
                                    This page
                                    requires {allowedRoles.length === 1 ? 'a' : 'one of the following'} role{allowedRoles.length > 1 ? 's' : ''}:
                                    <span className="font-medium"> {allowedRoles.join(', ')}</span>
                                </p>
                                <p className="text-murram-600 text-sm mt-1">
                                    Your current role: <span className="font-medium">{userRole}</span>
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="text-sm text-soil-600">
                                <h4 className="font-medium text-soil-900 mb-2">What you can do:</h4>
                                <ul className="space-y-1">
                                    <li>• Contact your administrator to request access</li>
                                    <li>• Return to your dashboard</li>
                                    <li>• Log in with a different account</li>
                                </ul>
                            </div>

                            <div className="flex flex-col space-y-3">
                                <button
                                    onClick={() => window.history.back()}
                                    className="flex items-center justify-center px-4 py-2 border border-soil-100 rounded-lg text-soil-600 bg-white hover:bg-soil-50 transition-colors duration-200"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-2"/>
                                    Go Back
                                </button>

                                <button
                                    onClick={() => {
                                        // Redirect based on user role
                                        const dashboardRoutes = {
                                            'admin': '/admin',
                                            'manager': '/manager',
                                            'tenant': '/dashboard'
                                        };
                                        window.location.href = dashboardRoutes[userRole] || '/';
                                    }}
                                    className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600 transition-colors duration-200"
                                >
                                    Go to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-soil-600">
                            Need help?{' '}
                            <button
                                onClick={() => window.location.href = '/contact'}
                                className="font-medium text-highland-500 hover:text-highland-600"
                            >
                                Contact Support
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // If user has required role, render the protected component
    return children;
};

export default RoleBasedRoute;